import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IsIPhoneSafari } from '@/components/utils/DeviceUtils/DeviceUtils';

import './Button.scss';

const propTypes = {
    animate: PropTypes.oneOf([null, 'in', 'out']),
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    href: PropTypes.string,
    id: PropTypes.string,
    onClick: PropTypes.func,
    openInNewTab: PropTypes.bool,
    largeHitbox: PropTypes.string,
    type: PropTypes.oneOf(['button', 'submit']),
    variant: PropTypes.oneOf([
        'base',
        'dialog',
        'primary-gold-light',
        'primary-dark',
        'primary-light',
        'primary-activate',
        'secondary-light',
        'secondary-dark',
        'secondary-gold',
        'icon-light',
        'icon-dark',
        'icon-gold',
        'icon-secondary-gold',
    ]),
    icon: PropTypes.node,
    iconSmall: PropTypes.bool,
};

const defaultProps = {
    animate: null,
    children: null,
    className: null,
    disabled: false,
    href: undefined,
    id: null,
    onClick: null,
    openInNewTab: false,
    type: 'button',
    variant: 'base',
    icon: false,
    iconSmall: true,
};

const Button = ({
 animate, children, className, disabled, href, id, onClick, openInNewTab, type, variant, icon, iconSmall, largeHitbox, ...props
}) => {
    const classes = classNames({
        Button: true,
        [`Button--${variant}`]: variant !== 'base',
        'Button--disabled': disabled,
        [`animate-${animate}`]: animate !== null,
        [`${className}`]: !!className,
        'Button--has-icon': !!icon && variant !== 'dialog',
        'Button--icon-small': !!icon && !!iconSmall && variant !== 'dialog',
    });

    const buttonEnds = classNames({
        [`Button--${variant}-ends`]: variant === 'primary-gold-light',
    });

    const Icon = ({ ...iconProps }) => React.cloneElement(icon, { ...iconProps });

    if (href === undefined) {
        // normal button
        /* eslint-disable react/button-has-type */
        return (
            <button disabled={disabled} className={classes} id={id} type={type} onClick={onClick} {...props}>
                {icon && <Icon className="Button__icon" />}
                <span>{children}</span>
                {(variant === 'primary-gold-light' || variant === 'primary-gold-dark') && <i className={buttonEnds} />}
                {largeHitbox && IsIPhoneSafari() && <span className="Button__largeHitbox" />}
            </button>
        );
        /* eslint-enable react/button-has-type */
    }
    const rel = 'rel="noopener noreferrer"';

    // link button
    return (
        <a className={classes} href={href} target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? rel : ''} onClick={onClick} {...props}>
            {icon && <Icon className="Button__icon" />}
            <span>{children}</span>
            {(variant === 'primary-gold-light' || variant === 'primary-gold-dark') && <i className={buttonEnds} />}
            {largeHitbox && IsIPhoneSafari() && <span className="Button__largeHitbox" />}
        </a>
    );
};

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
